import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import InfoSection, {
  InfoSectionTitle,
} from '@/components/Approval/@components/InfoSection';
import classNames from 'classnames';
import {
  ApprovalFormTable,
  ApprovalTableCalculatedCell,
  ApprovalTableUnit,
} from '@/components/Approval/@components/ApprovalFormTable';
import type { MortgageFeeFormState } from '@/utils/approval/mortgageFee';
import { FixedText, MortgageFeeRoot, NoteList, SectionHeader } from './styles';

export type MortgageFeeCellKey =
  | keyof MortgageFeeFormState
  | 'collateralTotalFeeAmount'
  | 'collateralHqBurdenAmount';

type Column = { key: MortgageFeeCellKey; label: string };

const STORE_COLUMNS: Column[] = [
  { key: 'brand', label: '브랜드' },
  { key: 'storeName', label: '매장명' },
  { key: 'storeOpenedAt', label: '오픈일자' },
  { key: 'storeOwnerName', label: '점주명' },
];

const COLLATERAL_COLUMNS: Column[] = [
  { key: 'collateralType', label: '물건종류' },
  { key: 'collateralValuationAmount', label: '평가총액' },
  { key: 'collateralDataSource', label: '자료출처' },
  { key: 'collateralPriorLienAmount', label: '선순위금액' },
  { key: 'collateralLienPriority', label: '당사담보순위' },
  { key: 'collateralMaximumSecuredAmount', label: '담보설정액' },
  { key: 'collateralRegisteredAt', label: '담보설정일자' },
];

const FEE_COLUMNS: Column[] = [
  { key: 'collateralTotalFeeAmount', label: '총비용' },
  { key: 'collateralNetFeeAmount', label: '공급가' },
  { key: 'collateralVatAmount', label: '부가세' },
  { key: 'collateralHqBurdenAmount', label: '본사 부담' },
];

const CALCULATED_KEYS = new Set<MortgageFeeCellKey>([
  'collateralTotalFeeAmount',
  'collateralHqBurdenAmount',
]);

type Props = {
  renderCell: (key: MortgageFeeCellKey) => ReactNode;
  /** 작성 화면 — 셀을 입력칸으로 채움, 총비용·본사 부담은 회색 계산 칸 */
  editable?: boolean;
  /** 작성 화면 계산 칸에 값이 있는지 — 없으면 흐린 글씨 */
  hasCalculated?: boolean;
};

/** (구)근저당설정비용 지급(81) 본문 표 — 작성·조회 공용, 레거시와 같은 표 구성 */
const MortgageFeeTables = ({
  renderCell,
  editable = false,
  hasCalculated = false,
}: Props) => {
  const { t } = useTranslation();

  const viewAlign = (className: string) => (editable ? undefined : className);

  const headRow = (columns: Column[]) => (
    <tr>
      {columns.map((c) => (
        <th key={c.key}>{t(c.label)}</th>
      ))}
    </tr>
  );

  return (
    <MortgageFeeRoot>
      <InfoSection title={t('내용', { context: 'detail' })}>
        <FixedText>
          {t(
            '공정위가 표준계약서에 규정한 내용 및 당사 대리점 계약서에 의해 부동산 담보 설정 비용을 본사가 일부 지원',
          )}
        </FixedText>
      </InfoSection>

      <InfoSection title={t('매장정보')}>
        <ApprovalFormTable editable={editable}>
          <thead>{headRow(STORE_COLUMNS)}</thead>
          <tbody>
            <tr>
              {STORE_COLUMNS.map((c) => (
                <td key={c.key} className={viewAlign('align-center')}>
                  {renderCell(c.key)}
                </td>
              ))}
            </tr>
          </tbody>
        </ApprovalFormTable>
      </InfoSection>

      <InfoSection>
        <SectionHeader>
          <InfoSectionTitle>{t('담보내용')}</InfoSectionTitle>
          <ApprovalTableUnit>{t('(단위 : 원)')}</ApprovalTableUnit>
        </SectionHeader>
        <ApprovalFormTable editable={editable}>
          <thead>{headRow(COLLATERAL_COLUMNS)}</thead>
          <tbody>
            <tr>
              {COLLATERAL_COLUMNS.map((c) => (
                <td key={c.key} className={viewAlign('align-center')}>
                  {renderCell(c.key)}
                </td>
              ))}
            </tr>
            <tr>
              <th>{t('담보주소')}</th>
              <td colSpan={6}>{renderCell('collateralAddress')}</td>
            </tr>
          </tbody>
        </ApprovalFormTable>
      </InfoSection>

      <InfoSection>
        <SectionHeader>
          <InfoSectionTitle>{t('설정비용')}</InfoSectionTitle>
          <ApprovalTableUnit>{t('(단위 : 원)')}</ApprovalTableUnit>
        </SectionHeader>
        <ApprovalFormTable editable={editable}>
          <thead>{headRow(FEE_COLUMNS)}</thead>
          <tbody>
            <tr>
              {FEE_COLUMNS.map((c) =>
                editable && CALCULATED_KEYS.has(c.key) ? (
                  <ApprovalTableCalculatedCell
                    key={c.key}
                    className={classNames(!hasCalculated && 'is-empty')}
                  >
                    {renderCell(c.key)}
                  </ApprovalTableCalculatedCell>
                ) : (
                  <td key={c.key} className={viewAlign('align-right')}>
                    {renderCell(c.key)}
                  </td>
                ),
              )}
            </tr>
          </tbody>
        </ApprovalFormTable>
        <NoteList>
          <li>
            {t(
              '* 담보설정 비용 : 등기 신청 수수료 및 법무사 수수료, 등록세 및 지방교육세 등',
            )}
          </li>
          <li>
            {t(
              '* 부가세는 부가가치세 신고납부시 대리점에서 공제를 받으므로 청구금액에서 제외됨',
            )}
          </li>
        </NoteList>
      </InfoSection>

      <InfoSection title={t('지급방법')}>
        <FixedText>
          {t('대리점주가 선결제 후 본사가 본사 부담금액 대리점주에게 입금')}
        </FixedText>
        <NoteList>
          <li>{t('* 첨부 : 증빙사본, 청구서, 통장사본')}</li>
        </NoteList>
      </InfoSection>
    </MortgageFeeRoot>
  );
};

export default MortgageFeeTables;
